import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Shield, Brain, Lock, Eye, Zap, Network, Activity, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import cognisafeLogo from '../assets/cognisafe_logo.png';
import OptimizedImage from './ui/OptimizedImage';

// Memoized constants to prevent recreation on every render
const navItems = [
  { name: 'Ana Sayfa', href: '#hero', icon: Shield },
  { name: 'Özellikler', href: '#features', icon: Brain },
  { name: 'Hakkımızda', href: '#about', icon: Eye },
  { name: 'Çözümler', href: '#solutions', icon: Network },
  { name: 'İletişim', href: '#contact', icon: Activity },
];

// Generate nodes once and reuse
const headerNodes = Array.from({ length: 8 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  delay: Math.random() * 3,
}));

const securityIcons = [Shield, Lock, Brain, Cpu, Network, Zap];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    const handleMouseMove = (e) => {
      setMousePosition({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const scrollToSection = useCallback((href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  }, []);

  // Memoize header class to prevent recalculation
  const headerClassName = useMemo(() => 
    `fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-lg shadow-gray-900/5' 
        : 'bg-transparent'
    }`, [isScrolled]
  );

  // Memoize grid background style
  const gridBackgroundStyle = useMemo(() => ({
    backgroundImage: `
      linear-gradient(rgba(59, 130, 246, 0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(59, 130, 246, 0.08) 1px, transparent 1px)
    `,
    backgroundSize: '30px 30px',
    transform: `translate(${mousePosition.x * 10}px, ${mousePosition.y * 5}px)`,
  }), [mousePosition.x, mousePosition.y]);

  return (
    <>
      {/* Neural Network Background Layer */}
      <div className="fixed top-0 left-0 right-0 h-24 z-40 pointer-events-none overflow-hidden">
        <svg className="w-full h-full opacity-20" viewBox="0 0 1200 100">
          <defs>
            <linearGradient id="headerNodeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.3" />
            </linearGradient>
            <radialGradient id="headerNodeFill" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.2" />
            </radialGradient>
          </defs>
          
          {/* Neural connections */}
          {headerNodes.map((node, i) => 
            headerNodes.slice(i + 1).map((targetNode, j) => {
              const distance = Math.sqrt(
                Math.pow(node.x - targetNode.x, 2) + Math.pow(node.y - targetNode.y, 2)
              );
              if (distance < 50) {
                return (
                  <motion.line
                    key={`${i}-${j}`}
                    x1={`${node.x * 12}px`}
                    y1={`${node.y}px`}
                    x2={`${targetNode.x * 12}px`}
                    y2={`${targetNode.y}px`}
                    stroke="url(#headerNodeGradient)"
                    strokeWidth="1"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.4 }}
                    transition={{
                      duration: 3,
                      delay: Math.max(node.delay, targetNode.delay),
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  />
                );
              }
              return null;
            })
          )}

          {/* Neural nodes */}
          {headerNodes.map((node, i) => (
            <motion.circle
              key={i}
              cx={`${node.x * 12}px`}
              cy={`${node.y}px`}
              r="2"
              fill="url(#headerNodeFill)"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.8, 0.3] }}
              transition={{
                duration: 4,
                delay: node.delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </svg>
      </div>

      {/* Main Header */}
      <motion.header
        className={headerClassName}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Holographic Grid Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={gridBackgroundStyle} />
        </div>

        {/* Floating Security Icons */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {securityIcons.map((Icon, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${15 + (i * 12)}%`,
                top: '50%',
              }}
              animate={{
                y: [-5, 5, -5],
                rotate: [0, 2, 0, -2, 0],
                scale: [0.8, 1, 0.8],
                opacity: [0.05, 0.15, 0.05],
              }}
              transition={{
                duration: 8 + i,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.3,
              }}
            >
              <div className="p-2 bg-blue-50/60 rounded-lg backdrop-blur-sm border border-blue-100/40">
                <Icon className="w-4 h-4 text-blue-400/60" />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center justify-between h-20">
            {/* Enhanced Logo */}
            <motion.div
              className="flex items-center space-x-4"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <div className="relative">
                {/* Professional Logo Container */}
                <motion.div
                  className="relative w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl border border-blue-200/40 backdrop-blur-sm shadow-sm"
                  animate={{
                    boxShadow: [
                      '0 2px 8px rgba(59, 130, 246, 0.08)',
                      '0 4px 12px rgba(59, 130, 246, 0.12)',
                      '0 2px 8px rgba(59, 130, 246, 0.08)',
                    ],
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <OptimizedImage 
                    src={cognisafeLogo} 
                    alt="Cognisafe - AI Destekli İnsan Odaklı Siber Güvenlik Platformu Logo" 
                    className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    loading="eager"
                    width={32}
                    height={32}
                  />
                  
                  {/* Subtle pulse effect */}
                  <motion.div
                    className="absolute inset-0 rounded-xl border border-blue-200/40"
                    animate={{
                      scale: [1, 1.05, 1],
                      opacity: [0.2, 0.05, 0.2],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
              </div>
              
              <div className="flex flex-col">
                <motion.span 
                  className="text-2xl font-heading font-black bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 bg-clip-text text-transparent"
                  animate={{
                    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  Cognisafe
                </motion.span>
                <span className="text-xs text-blue-500/70 font-sans tracking-wider">CYBER INTELLIGENCE</span>
              </div>
            </motion.div>

            {/* Enhanced Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.name}
                    onClick={() => scrollToSection(item.href)}
                    className="group relative px-6 py-3 transition-all duration-300 font-sans font-medium overflow-hidden block text-gray-700 hover:text-blue-600"
                  >
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="w-full h-full"
                    >
                    {/* Holographic hover effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-400/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      whileHover={{
                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.2)',
                      }}
                    />
                    
                    {/* Neural connection lines */}
                    <motion.div
                      className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 w-0 group-hover:w-full transition-all duration-300"
                    />
                    
                      <div className="relative z-10 flex items-center space-x-2">
                        <Icon className={`w-4 h-4 transition-opacity ${
                          location.pathname === item.href 
                            ? 'opacity-100' 
                            : 'opacity-60 group-hover:opacity-100'
                        }`} />
                        <span>{item.name}</span>
                      </div>
                    </motion.div>
                  </button>
                );
              })}
            </nav>

            {/* Enhanced CTA Button */}
            <div className="hidden lg:flex items-center space-x-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  onClick={() => scrollToSection('#contact')}
                  className="group relative px-8 py-3 bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-cyan-400 hover:to-blue-500 text-white rounded-xl font-sans font-bold text-sm overflow-hidden transition-all duration-300 border border-blue-400/30"
                >
                  {/* Neural pulse background */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    animate={{
                      scale: [1, 1.05, 1],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  
                  <span className="relative z-10 flex items-center">
                    <Brain className="mr-2 w-4 h-4" />
                    Demo Talep Et
                  </span>
                  
                  {/* Holographic border effect */}
                  <motion.div
                    className="absolute inset-0 rounded-xl border border-blue-300/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    animate={{
                      boxShadow: [
                        '0 0 10px rgba(59, 130, 246, 0.3)',
                        '0 0 20px rgba(6, 182, 212, 0.4)',
                        '0 0 10px rgba(59, 130, 246, 0.3)',
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </Button>
              </motion.div>
            </div>

            {/* Enhanced Mobile Menu Button */}
            <motion.button
              className="lg:hidden relative p-3 rounded-xl bg-white/80 border border-blue-200/50 text-blue-600 hover:text-blue-700 transition-all duration-300 backdrop-blur-sm shadow-sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={{ rotate: isMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </motion.div>
              
              {/* Neural pulse effect */}
              <motion.div
                      className="absolute inset-0 rounded-xl border border-blue-300/40 opacity-0 hover:opacity-100 transition-opacity duration-300"
                      animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0, 0.3, 0],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
            </motion.button>
          </div>

          {/* Enhanced Mobile Navigation */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                className="lg:hidden absolute top-full left-4 right-4 mt-2"
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-blue-200/30 shadow-2xl shadow-gray-500/10 overflow-hidden">
                  {/* Mobile menu neural background */}
                  <div className="absolute inset-0 opacity-5">
                    <div 
                      className="absolute inset-0"
                      style={{
                        backgroundImage: `
                          linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
                        `,
                        backgroundSize: '20px 20px',
                      }}
                    />
                  </div>
                  
                  <div className="relative z-10 py-6 space-y-2">
                    {navItems.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <motion.button
                          key={item.name}
                          onClick={() => scrollToSection(item.href)}
                          className="group w-full flex items-center px-6 py-4 text-gray-700 hover:text-blue-600 hover:bg-blue-50/80 transition-all duration-300 font-sans"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ x: 10 }}
                        >
                          <div className="flex items-center space-x-4">
                            <div className="w-8 h-8 bg-blue-100/80 rounded-lg flex items-center justify-center group-hover:bg-blue-200/80 transition-colors">
                              <Icon className="w-4 h-4" />
                            </div>
                            <span className="font-medium">{item.name}</span>
                          </div>
                          
                          {/* Neural connection line */}
                          <motion.div
                            className="ml-auto w-0 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 group-hover:w-8 transition-all duration-300"
                          />
                        </motion.button>
                      );
                    })}
                    
                    <div className="px-6 pt-4 border-t border-blue-200/30">
                      <Button
                        onClick={() => scrollToSection('#contact')}
                        className="w-full bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-cyan-400 hover:to-blue-500 text-white py-3 rounded-xl font-sans font-bold transition-all duration-300"
                      >
                        <Brain className="mr-2 w-4 h-4" />
                        Demo Talep Et
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.header>
    </>
  );
};

export default memo(Header);

